/**
 * useMcpTool — fetch-on-mount + manual-refresh hook over mcpCallTool.
 * Deliberately tiny (no react-query); the platform has ~10 call sites.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { McpFailReason, McpService, mcpCallTool } from "./mcp";

export interface McpToolState<T> {
  data: T | null;
  error: { reason: McpFailReason; detail?: string } | null;
  loading: boolean;
  refresh: () => void;
}

export function useMcpTool<T = unknown>(
  service: McpService,
  tool: string,
  args: Record<string, unknown> = {},
  opts: { enabled?: boolean; timeoutMs?: number } = {},
): McpToolState<T> {
  const { enabled = true, timeoutMs } = opts;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<{ reason: McpFailReason; detail?: string } | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  // Stable identity for the args object so callers can inline literals.
  const argsKey = useMemo(() => JSON.stringify(args), [args]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return;
    setLoading(true);
    const parsedArgs = JSON.parse(argsKey) as Record<string, unknown>;
    mcpCallTool<T>(service, tool, parsedArgs, timeoutMs).then((result) => {
      if (!alive.current) return;
      if (result.ok) {
        setData(result.data);
        setError(null);
      } else {
        setError({ reason: result.reason, detail: result.detail });
      }
      setLoading(false);
    });
    return () => {
      alive.current = false;
    };
  }, [service, tool, argsKey, enabled, timeoutMs, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return { data, error, loading, refresh };
}
